import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Modal, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import ChestImage from '../../../assets/dailyMissions/chest.png';
import CloseIcon from '../../../assets/dailyMissions/close.svg';
import GiftImage from '../../../assets/dailyMissions/gift.png';
import ResetClockIcon from '../../../assets/dailyMissions/reset-clock.svg';
import TargetIcon from '../../../assets/header/target.svg';
import { goldGlow } from '../../../shared/glowStyles';
import type { RootState } from '../../../store';
import {
	missionChestClaimRequest,
	missionClaimRequest,
	MISSIONS_CHEST_TARGET,
} from '../../../store/missions/missionsActions';
import {
	selectMissionsClaimError,
	selectMissionsClaimingTargets,
	selectMissionsCompletion,
	selectMissionsCycleKey,
	selectMissionsHubErrorKey,
	selectMissionsHubLoading,
	selectMissionsLastClaim,
	selectMissionsList,
	selectMissionsResetAt,
} from '../../../store/missions/missionsReducer';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { getWalletPoint } from '../../shell/header/walletPosition';
import { GoldGradientText } from '../common/GoldGradientText';
import { Pressable } from '../common/Touchable';
import { DailyMissionClaimAnimation } from './DailyMissionsComponents/DailyMissionClaimAnimation';
import {
	DailyMission,
	DailyMissionRow,
	MISSION_ROW_HEIGHT,
} from './DailyMissionsComponents/DailyMissionRow';
import { missionText, toDailyMissionView } from './DailyMissionsComponents/missionsViewModel';
import { useRedesignScale } from './useRedesignScale';

// Figma: "Daily Missions / Modal" (3085:17034) - a 366x488 panel on the 390-wide mobile frame.
// Every number below is in that design space and scaled to the viewport once.
const DESIGN_FRAME_WIDTH = 390;
const DESIGN_MODAL_WIDTH = 366;
const DESIGN_MODAL_HEIGHT = 488;
const DESIGN_CONTENT_WIDTH = 342;
/**
 * The title row: the target icon and the title centred as one group, the 44 close button pinned
 * right. The title may take what is left once the group stays clear of the close button on both
 * sides: 342 - 2 x (44 + 8) - 40 - 8.
 */
const TITLE = { fontSize: 22, height: 44, icon: 40, gap: 8, close: 44 } as const;
const TITLE_MAX_WIDTH =
	DESIGN_CONTENT_WIDTH - 2 * (TITLE.close + TITLE.gap) - TITLE.icon - TITLE.gap;
const MAX_SCALE = 1.15;
// Clearance the panel keeps from the top and bottom edges when the viewport is too short for
// the design's own height (landscape, small phones).
const VIEWPORT_MARGIN = 48;

const FONT = 'Sofia Sans';

const colors = {
	backdrop: 'rgba(0,0,0,0.68)',
	panelBorder: '#FFF908',
	panelBase: '#320400',
	surface: '#1A0603',
	white: '#FFFFFF',
	accent: '#FFFF99',
	completionBorder: '#DA600E',
	progressTrack: '#4A1A0E',
	progressGold: '#FFC500',
	claimBorder: 'rgba(184,255,140,0.75)',
	claimFill: '#248F1C',
} as const;

const PANEL_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.80127', '#320400'],
	['1', '#320400'],
];
const COMPLETION_FILL: Array<[string, string]> = [
	['0', '#970501'],
	['1', '#220802'],
];

const COUNTDOWN_PLACEHOLDER = '--:--:--';
const pad = (value: number) => String(value).padStart(2, '0');

const useResetCountdown = (resetAt: string | null, active: boolean) => {
	const deadline = useMemo(() => {
		if (!resetAt) return null;

		const parsed = Date.parse(resetAt);
		return Number.isNaN(parsed) ? null : parsed;
	}, [resetAt]);

	const [label, setLabel] = useState(COUNTDOWN_PLACEHOLDER);

	useEffect(() => {
		if (!active || deadline === null) {
			setLabel(COUNTDOWN_PLACEHOLDER);
			return;
		}

		const tick = () => {
			const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
			setLabel(
				`${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`,
			);
			return seconds;
		};

		if (tick() <= 0) return;
		const interval = setInterval(() => {
			if (tick() <= 0) clearInterval(interval);
		}, 1000);
		return () => clearInterval(interval);
	}, [active, deadline]);

	return label;
};

type Props = {
	visible: boolean;
	onClose: () => void;
};

const DailyMissionsModal = ({ visible, onClose }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { width, height } = useSafeViewport();
	const { isDesktop } = useDevice();
	const panelId = useSvgId('dailyMissionsPanel');
	const completionId = useSvgId('dailyMissionsCompletion');

	const missionDtos = useSelector(selectMissionsList);
	const completion = useSelector(selectMissionsCompletion);
	const cycleKey = useSelector(selectMissionsCycleKey);
	const resetAt = useSelector(selectMissionsResetAt);
	const hubLoading = useSelector(selectMissionsHubLoading);
	const hubErrorKey = useSelector(selectMissionsHubErrorKey);
	const claimingTargets = useSelector(selectMissionsClaimingTargets);
	const lastClaim = useSelector(selectMissionsLastClaim);
	const claimError = useSelector(selectMissionsClaimError);
	const accountId = useSelector((state: RootState) => state.auth.user?.id);
	const chips = useSelector((state: RootState) => state.auth.user?.chips ?? 0);
	const [optimisticClaims, setOptimisticClaims] = useState<Set<string>>(() => new Set());

	const resetLabel = useResetCountdown(resetAt, visible);

	const settledTarget = lastClaim?.target ?? claimError?.target;

	const missions = useMemo(
		() =>
			missionDtos.map(dto => {
				const mission = toDailyMissionView(dto, {
					busy: !!claimingTargets[dto.id] || (hubLoading && settledTarget === dto.id),
				});
				return optimisticClaims.has(dto.id)
					? { ...mission, claimable: false, claimed: true, busy: false }
					: mission;
			}),
		[missionDtos, claimingTargets, hubLoading, optimisticClaims, settledTarget],
	);

	const reserveAction = missions.some(mission => mission.claimable || mission.claimed);

	const chestBusy =
		!!claimingTargets[MISSIONS_CHEST_TARGET] ||
		(hubLoading && settledTarget === MISSIONS_CHEST_TARGET);

	const handleClaim = (mission: DailyMission, source: { x: number; y: number }) => {
		const wallet = getWalletPoint();
		if (wallet) {
			setClaimAnimation({
				id: Date.now(),
				source,
				wallet,
				amount: Number(mission.rewardLabel?.replace(/,/g, '') ?? 0),
				initialChips: chips,
			});
		}
		if (!cycleKey) return;
		setOptimisticClaims(current => new Set(current).add(mission.id));

		dispatch(missionClaimRequest({ missionId: mission.id, cycleKey }));
	};

	const handleChestClaim = () => {
		if (!cycleKey) return;

		dispatch(missionChestClaimRequest({ cycleKey }));
	};

	const claimOutcomeKey =
		lastClaim?.outcome === 'partial'
			? 'T_SITE_MISSIONS_REWARD_PARTIAL'
			: lastClaim?.outcome === 'alreadyClaimed'
				? 'T_SITE_MISSIONS_CLAIMED'
				: undefined;

	const noticeKey =
		claimError?.key ?? claimOutcomeKey ?? (missionDtos.length > 0 ? hubErrorKey : undefined);

	const showLoadError = !!hubErrorKey && missionDtos.length === 0;
	const showSpinner = hubLoading && missionDtos.length === 0 && !hubErrorKey;
	const showEmpty = !hubLoading && !hubErrorKey && missionDtos.length === 0;

	// On the desktop frame the modal is a dialog at the shared desktop modal scale.
	const desktopModalScale = useRedesignScale({ fitHeight: DESIGN_MODAL_HEIGHT });
	const scale = isDesktop
		? Math.min(desktopModalScale, (height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT)
		: Math.min(
				width / DESIGN_FRAME_WIDTH,
				(height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT,
				MAX_SCALE,
			);
	const styles = useMemo(() => createStyles(scale), [scale]);
	const title = t('T_SITE_MISSIONS_TITLE', { defaultValue: 'DAILY MISSIONS' });
	// The gradient title is SVG text, which cannot size itself, so a hidden copy measures it - the
	// group can then be centred on the title's real width in every language.
	const [titleWidth, setTitleWidth] = useState<number | null>(null);
	const [claimAnimation, setClaimAnimation] = useState<{
		id: number;
		source: { x: number; y: number };
		wallet: { x: number; y: number; scale?: number };
		amount: number;
		initialChips: number;
	} | null>(null);

	useEffect(() => {
		setOptimisticClaims(new Set());
		setClaimAnimation(null);
	}, [accountId]);

	useEffect(() => {
		if (!claimAnimation) return;
		const timer = setTimeout(() => setClaimAnimation(null), 2020);
		return () => clearTimeout(timer);
	}, [claimAnimation]);

	useEffect(() => {
		if (!claimError || claimError.target === MISSIONS_CHEST_TARGET) return;
		setOptimisticClaims(current => {
			const next = new Set(current);
			next.delete(claimError.target);
			return next;
		});
	}, [claimError]);
	const titleMaxWidth = TITLE_MAX_WIDTH * scale;
	const titleBoxWidth = Math.min(titleWidth ?? titleMaxWidth, titleMaxWidth);
	const contentWidth = DESIGN_CONTENT_WIDTH * scale;
	// SVG components take numbers, not styles, so the icon sizes live outside the sheet.
	const icons = {
		target: TITLE.icon * scale,
		close: 18 * scale,
		clock: 20 * scale,
	};

	const completed = missionDtos.filter(mission => mission.completed).length;
	const completionRatio = missionDtos.length > 0 ? completed / missionDtos.length : 0;

	return (
		<Modal
			visible={visible}
			transparent
			animationType='fade'
			onRequestClose={onClose}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={styles.overlay}>
				<DailyMissionClaimAnimation request={claimAnimation} />
				<Pressable accessibilityRole='button' onPress={onClose} style={styles.backdrop} />

				<View style={styles.panel}>
					<View pointerEvents='none' style={styles.panelFill}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={panelId} x1='0' y1='0' x2='0' y2='1'>
									{PANEL_FILL.map(([offset, color]) => (
										<Stop key={offset} offset={offset} stopColor={color} />
									))}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${panelId})`} />
						</Svg>
					</View>

					<View style={styles.header}>
						<View style={styles.titleRow}>
							<Text
								aria-hidden
								importantForAccessibility='no-hide-descendants'
								numberOfLines={1}
								onLayout={event => setTitleWidth(event.nativeEvent.layout.width)}
								style={styles.titleMeasure}
							>
								{title}
							</Text>
							<TargetIcon width={icons.target} height={icons.target} />
							<GoldGradientText
								align='left'
								text={title}
								width={titleBoxWidth}
								height={TITLE.height * scale}
								fontSize={TITLE.fontSize * scale}
								// A title longer than the room left is squeezed to fit rather than run
								// under the close button.
								textLength={
									titleWidth != null && titleWidth > titleMaxWidth ? titleMaxWidth : undefined
								}
							/>
							<Pressable
								accessibilityRole='button'
								onPress={onClose}
								style={styles.closeHitArea}
								hitSlop={8}
							>
								<View style={styles.closeCircle}>
									<CloseIcon width={icons.close} height={icons.close} />
								</View>
							</Pressable>
						</View>

						<Text style={styles.description}>
							{t('T_SITE_MISSIONS_DESCRIPTION', {
								defaultValue: 'Complete missions every day and earn amazing rewards!',
							})}
						</Text>

						<View style={styles.timerRow}>
							<View style={styles.resetPill}>
								<ResetClockIcon width={icons.clock} height={icons.clock} />
								<Text style={styles.resetLabel}>
									{t('T_SITE_MISSIONS_RESETS_IN', { defaultValue: 'Resets in:' })}
								</Text>
								<Text style={styles.resetTime}>{resetLabel}</Text>
							</View>
						</View>

						{noticeKey ? (
							<Text numberOfLines={2} style={styles.notice}>
								{missionText(t, noticeKey)}
							</Text>
						) : null}
					</View>

					<View style={styles.missions}>
						{showSpinner ? (
							<View style={styles.missionsPlaceholder}>
								<ActivityIndicator color={colors.accent} />
							</View>
						) : null}

						{showLoadError ? (
							<View style={styles.missionsPlaceholder}>
								<Text style={styles.placeholderText}>
									{missionText(t, hubErrorKey ?? 'T_SITE_MISSIONS_ERROR_LOAD')}
								</Text>
							</View>
						) : null}

						{showEmpty ? (
							<View style={styles.missionsPlaceholder}>
								<Text style={styles.placeholderText}>
									{t('T_SITE_MISSIONS_EMPTY', {
										defaultValue: 'No missions today. Come back tomorrow.',
									})}
								</Text>
							</View>
						) : null}

						{missions.map(mission => (
							<DailyMissionRow
								key={mission.id}
								mission={mission}
								width={contentWidth}
								claimLabel={t('T_SITE_MISSIONS_CLAIM', { defaultValue: 'CLAIM' })}
								claimedLabel={t('T_SITE_MISSIONS_CLAIMED', { defaultValue: 'CLAIMED' })}
								reserveAction={reserveAction}
								onClaim={handleClaim}
							/>
						))}
					</View>

					{missionDtos.length > 0 ? (
						<View style={styles.completion}>
							<View pointerEvents='none' style={styles.completionFill}>
								<Svg width='100%' height='100%'>
									<Defs>
										<LinearGradient id={completionId} x1='0' y1='0' x2='0' y2='1'>
											{COMPLETION_FILL.map(([offset, color]) => (
												<Stop key={offset} offset={offset} stopColor={color} />
											))}
										</LinearGradient>
									</Defs>
									<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${completionId})`} />
								</Svg>
							</View>

							<Image source={ChestImage} style={styles.chest} resizeMode='contain' />
							{/* Two lines, the way the design breaks the sentence. */}
							<View style={styles.completionMessage}>
								{completion?.available ? (
									<Text style={styles.completionMessageLine}>
										{t('T_SITE_MISSIONS_CHEST_READY', {
											defaultValue: 'Your daily reward is ready',
										})}
									</Text>
								) : (
									<>
										<Text style={styles.completionMessageLine}>
											{t('T_SITE_MISSIONS_COMPLETE_ALL', {
												defaultValue: 'Complete all daily missions',
											})}
										</Text>
										<Text style={styles.completionMessageLine}>
											{t('T_SITE_MISSIONS_SPECIAL_REWARD', {
												defaultValue: 'and get a special reward!',
											})}
										</Text>
									</>
								)}
							</View>

							{completion?.claimed ? (
								<View style={styles.completionProgress}>
									<Text style={styles.completionCount}>
										{t('T_SITE_MISSIONS_CLAIMED', { defaultValue: 'CLAIMED' })}
									</Text>
								</View>
							) : completion?.available ? (
								<View style={styles.completionProgress}>
									<Pressable
										disabled={chestBusy}
										onPress={handleChestClaim}
										style={styles.chestAction}
									>
										{chestBusy ? (
											<ActivityIndicator color={colors.white} />
										) : (
											<Text numberOfLines={1} adjustsFontSizeToFit style={styles.chestActionLabel}>
												{t('T_SITE_MISSIONS_CLAIM', { defaultValue: 'CLAIM' })}
											</Text>
										)}
									</Pressable>
								</View>
							) : (
								<View style={styles.completionProgress}>
									<View style={styles.completionTrack}>
										<View
											style={[styles.completionFillBar, { width: 72 * scale * completionRatio }]}
										/>
									</View>
									<Text style={styles.completionCount}>
										{completed}/{missionDtos.length}
									</Text>
								</View>
							)}
							<Image source={GiftImage} style={styles.gift} resizeMode='contain' />
						</View>
					) : null}
				</View>
			</View>
		</Modal>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		overlay: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
		},
		backdrop: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: colors.backdrop,
		},
		panel: {
			width: s(DESIGN_MODAL_WIDTH),
			padding: s(12),
			gap: s(8),
			borderRadius: s(16),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: colors.panelBorder,
			// The gradient fill sits on top of this; the flat colour keeps the shadow layer opaque.
			backgroundColor: colors.panelBase,
			shadowColor: '#FFB800',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
			elevation: 12,
		},
		panelFill: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(16),
			overflow: 'hidden',
		},
		header: {
			width: s(DESIGN_CONTENT_WIDTH),
			gap: s(4),
		},
		titleRow: {
			height: s(TITLE.height),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(TITLE.gap),
		},
		titleMeasure: {
			position: 'absolute',
			left: 0,
			opacity: 0,
			fontFamily: 'Sofia Sans',
			fontSize: s(TITLE.fontSize),
			fontWeight: '700',
		},
		closeHitArea: {
			position: 'absolute',
			right: 0,
			width: s(TITLE.close),
			height: s(TITLE.close),
			alignItems: 'center',
			justifyContent: 'center',
		},
		closeCircle: {
			width: s(32),
			height: s(32),
			borderRadius: s(16),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.panelBorder,
			backgroundColor: colors.surface,
			alignItems: 'center',
			justifyContent: 'center',
			...goldGlow,
		},
		description: {
			width: s(DESIGN_CONTENT_WIDTH),
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(14.4),
			fontWeight: '500',
			color: colors.white,
			textAlign: 'center',
		},
		timerRow: {
			height: s(28),
			alignItems: 'center',
			justifyContent: 'center',
		},
		resetPill: {
			width: s(188),
			height: s(26),
			borderRadius: s(13),
			backgroundColor: colors.surface,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(4),
		},
		resetLabel: {
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(14.4),
			fontWeight: '500',
			color: colors.accent,
		},
		resetTime: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(14.132),
			fontWeight: '600',
			color: colors.accent,
		},
		notice: {
			width: s(DESIGN_CONTENT_WIDTH),
			fontFamily: FONT,
			fontSize: s(11),
			lineHeight: s(13.2),
			fontWeight: '600',
			color: colors.accent,
			textAlign: 'center',
		},
		missions: {
			width: s(DESIGN_CONTENT_WIDTH),
			gap: s(6),
		},
		missionsPlaceholder: {
			width: s(DESIGN_CONTENT_WIDTH),
			height: s(MISSION_ROW_HEIGHT * 2),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(16),
		},
		placeholderText: {
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(14.4),
			fontWeight: '500',
			color: colors.white,
			textAlign: 'center',
		},
		chestAction: {
			width: s(72),
			height: s(28),
			borderRadius: s(8),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.claimBorder,
			backgroundColor: colors.claimFill,
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(2),
		},
		chestActionLabel: {
			fontFamily: FONT,
			fontSize: s(13),
			lineHeight: s(13.045),
			fontWeight: '700',
			color: colors.white,
			letterSpacing: s(0.05),
		},
		completion: {
			width: s(DESIGN_CONTENT_WIDTH),
			height: s(58),
			borderRadius: s(12),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.completionBorder,
			backgroundColor: '#220802',
			flexDirection: 'row',
			alignItems: 'center',
			paddingLeft: s(8),
			paddingRight: s(16),
			gap: s(8),
			overflow: 'hidden',
		},
		completionFill: {
			...StyleSheet.absoluteFillObject,
		},
		chest: {
			width: s(52),
			height: s(48),
		},
		completionMessage: {
			width: s(138),
		},
		completionMessageLine: {
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(14.4),
			fontWeight: '500',
			color: colors.white,
		},
		completionProgress: {
			width: s(72),
			alignItems: 'center',
			gap: s(4),
		},
		completionTrack: {
			width: s(72),
			height: s(10),
			borderRadius: s(5),
			backgroundColor: colors.progressTrack,
			overflow: 'hidden',
		},
		completionFillBar: {
			height: s(10),
			borderRadius: s(5),
			backgroundColor: colors.progressGold,
		},
		completionCount: {
			fontFamily: FONT,
			fontSize: s(12),
			lineHeight: s(16.8),
			fontWeight: '600',
			color: colors.accent,
			letterSpacing: s(0.12),
		},
		gift: {
			width: s(32),
			height: s(34),
		},
	});
};

export default DailyMissionsModal;
